import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { LeadCTA, StickyMobileCTA } from "@/components/LeadCTA";
import { withLocale, SITE } from "@/lib/seo";
import { ARTICLE_MAP } from "@/data/knowledge";
import { SERVICE_MAP_SEO } from "@/data/servicesSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KnowledgeArticle() {
  const { slug } = useParams();
  const { t, lang, isAr } = useLang();
  const [state, setState] = useState({ loading: true, article: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, article: null });
    axios.get(`${API}/articles/${slug}`)
      .then((r) => { if (alive) setState({ loading: false, article: r.data }); })
      .catch(() => { if (alive) setState({ loading: false, article: ARTICLE_MAP[slug] || null }); });
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;
  }
  const a = state.article;
  if (!a) return <Navigate to={withLocale(lang, "/knowledge")} replace />;
  const svc = SERVICE_MAP_SEO[a.service];

  const crumbs = [
    { label: { en: "Home", ar: "الرئيسية" }, to: "/" },
    { label: { en: "Knowledge Center", ar: "مركز المعرفة" }, to: "/knowledge" },
    { label: a.title },
  ];
  const schema = [
    { "@context": "https://schema.org", "@type": "Article", headline: t(a.title), datePublished: a.date,
      inLanguage: lang, author: { "@type": "Organization", name: "Smart Account Vision" },
      publisher: { "@type": "Organization", name: "Smart Account Vision", logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
      mainEntityOfPage: `${SITE}/${lang}/knowledge/${slug}` },
    breadcrumbSchema(lang, crumbs),
  ];

  return (
    <div className="pt-28 lg:pt-32 pb-24">
      <Seo title={{ en: `${a.title.en} | Smart Account Vision`, ar: `${a.title.ar} | سمارت أكاونت فيجن` }} description={a.excerpt} schema={schema} type="article" />
      <article className="container-tnsq max-w-3xl" data-testid="knowledge-article">
        <Breadcrumbs items={crumbs} />
        <Reveal delay={60}><h1 className="font-display text-3xl sm:text-4xl text-ink mt-6 leading-[1.15]">{t(a.title)}</h1></Reveal>
        <div className="mt-3 text-sm text-muted flex items-center gap-3" dir="ltr">
          <span>{a.date}</span>
          {a.official && a.source_url && (
            <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brandgreen"><ShieldCheck className="h-3.5 w-3.5" /> {a.source_name || "Official source"}</a>
          )}
        </div>
        <Reveal delay={120}><p className="mt-6 text-lg text-ink/85 leading-relaxed">{t(a.excerpt)}</p></Reveal>
        <Reveal delay={160}><p className="mt-4 text-muted leading-relaxed whitespace-pre-line">{t(a.body)}</p></Reveal>
        {svc && (
          <p className="mt-6 text-muted">{t({ en: "See our service:", ar: "اطّلع على خدمتنا:" })} <Link to={withLocale(lang, `/services/${svc.slug}`)} className="font-semibold text-navy hover:text-crimson inline-flex items-center gap-1">{t(svc.h1)} <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} /></Link></p>
        )}
        <LeadCTA service={svc ? t(svc.h1) : undefined} />
      </article>
      <StickyMobileCTA />
    </div>
  );
}
